import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Combobox,
  type ComboboxInputPropsInterface,
  type ComboboxOptionType,
  Divider,
  DropdownSelect,
  FieldSet,
  FormField,
  Heading,
  Icon,
  Label,
  Legend,
  Modal,
  Paragraph,
  Switch,
  Text,
  TextButton,
  ToggleCard,
  useToast,
} from '@zillow/constellation-10';
import { IconArrowLeftOutline, IconArrowRightOutline, IconClockFilled } from '@zillow/constellation-icons';
import type { LocalTime, TourConfirmationType, TourDuration } from '@zg-rentals/ts-z-calendar-client';

import TourConfirmationTypeCard from '../../../TourConfirmationTypeCard';
import {
  TimeZoneDropdownOptions,
  type TimeZoneIds,
  TOUR_CONFIRMATION_TYPE_LAYOUT,
  TOUR_DURATION_DROPDOWN_OPTIONS,
  type TOUR_OPTION,
  TOUR_OPTIONS,
} from '../../../../constants/TourCalendar';
import { isTimeBeforeOrEqual } from '../../../../utils/availabilityTimeUtils';
import {
  convert12HourTimeToLocalTime,
  convertLocalTimeToDate,
  createCalendarTimeOptions,
  formatTimeString,
} from '../../../../utils/formatTime';
import UnmaskedToast from '../../../UnmaskedToast';

type CalendarSettingsProps = {
  timeZone: TimeZoneIds;
  tourConfirmationType?: TourConfirmationType;
  tourDuration?: TourDuration;
  canEditTourDuration: boolean;
  onSelectTourConfirmationType: (tourConfirmationType: TourConfirmationType) => void;
  onClickBack: () => void;
  isTouringSchedulingEnabled: boolean;
  onTourSchedulingToggle: () => void;
  isDisableTouringForListingLoading: boolean;
  calendarStartTime: LocalTime;
  calendarEndTime: LocalTime;
  onChangeCalendarConfigTime(startTime: LocalTime, endTime: LocalTime): void;
  onChangeTourDuration: (duration: TourDuration) => void;
};

const CalendarSettings = ({
  timeZone,
  tourConfirmationType,
  tourDuration,
  canEditTourDuration,
  onSelectTourConfirmationType,
  onClickBack,
  isTouringSchedulingEnabled,
  onTourSchedulingToggle,
  isDisableTouringForListingLoading,
  calendarStartTime,
  calendarEndTime,
  onChangeCalendarConfigTime,
  onChangeTourDuration,
}: CalendarSettingsProps) => {
  const { enqueueToast } = useToast();
  const [selectedStartTime, setSelectedStartTime] = useState(
    formatTimeString(convertLocalTimeToDate(calendarStartTime), { showMeridiem: true }),
  );
  const [selectedEndTime, setSelectedEndTime] = useState(
    formatTimeString(convertLocalTimeToDate(calendarEndTime), { showMeridiem: true }),
  );
  const [showTourDurationWarning, setShowTourDurationWarning] = useState<boolean>(false);
  const [selectedTourDuration, setSelectedTourDuration] = useState<TourDuration | undefined>(tourDuration);
  const [dropdownKey, setDropdownKey] = useState<number>(0);

  const timeZoneDisplayValue = useMemo(
    () => TimeZoneDropdownOptions.find((option) => option.value === timeZone)?.label,
    [timeZone],
  );

  const calendarTimeOptions = useMemo(() => createCalendarTimeOptions(), []);

  const handleClickToggleCard = (tourOption: TOUR_OPTION) => {
    if (tourOption.id === tourConfirmationType) {
      return;
    }
    return onSelectTourConfirmationType(tourOption.id);
  };

  const handleChangeStartTime = (option: ComboboxOptionType) => {
    if (typeof option === 'string') {
      setSelectedStartTime(option);

      if (isTimeBeforeOrEqual(selectedEndTime, option)) {
        enqueueToast(<UnmaskedToast tone="critical">The start time must be earlier than the end time</UnmaskedToast>);
        return;
      }

      const start = convert12HourTimeToLocalTime(option);
      const end = convert12HourTimeToLocalTime(selectedEndTime);
      onChangeCalendarConfigTime(start, end);
    }
  };

  const handleChangeEndTime = (option: ComboboxOptionType) => {
    if (typeof option === 'string') {
      setSelectedEndTime(option);

      if (isTimeBeforeOrEqual(option, selectedStartTime)) {
        enqueueToast(<UnmaskedToast tone="critical">The end time must be later than the start time</UnmaskedToast>);
        return;
      }
      const start = convert12HourTimeToLocalTime(selectedStartTime);
      const end = convert12HourTimeToLocalTime(option);
      onChangeCalendarConfigTime(start, end);
    }
  };

  const handleTourDurationChange = (duration: TourDuration) => {
    setSelectedTourDuration(duration);
    setShowTourDurationWarning(true);
  };

  const handleConfirmTourDuration = () => {
    onChangeTourDuration(selectedTourDuration!);
    setShowTourDurationWarning(false);
  };

  const handleCloseTourDurationWarningModal = () => {
    setShowTourDurationWarning(false);
    setSelectedTourDuration(tourDuration);
    setDropdownKey((prev) => prev + 1);
  };

  return (
    <Box data-testid="CalendarSettings">
      <Box
        css={{
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor:
            'gray.300' /* oxlint-disable-line constellation/prefer-semantic-tokens -- ambiguous semantic-token suggestion, not migrating in a lint opt-in */,
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 'xs',
          padding: { base: 'sm', md: 'md' },
        }}
      >
        <Box css={{ alignItems: 'center', display: 'flex', gap: 'loose' }}>
          <TextButton.Icon aria-label="Back to calendar" data-testid="CalendarSettings-BackBtn" onClick={onClickBack}>
            <IconArrowLeftOutline />
          </TextButton.Icon>
          <Text textStyle="heading-xs">Tour settings</Text>
        </Box>
      </Box>

      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'default',
          margin: { base: 'loose', md: 'looser' },
          // oxlint-disable-next-line constellation/no-raw-spacing-values
          maxWidth: '480px',
        }}
      >
        <Box css={{ display: 'flex', flexDirection: 'row', gap: 'loose', marginBottom: 'default' }}>
          <Label size="lg">Tour Scheduling</Label>
          <Switch
            aria-label="Tour Scheduling"
            data-testid="CalendarSettings-TourSchedulingSwitch"
            checked={isTouringSchedulingEnabled}
            disabled={isDisableTouringForListingLoading}
            onChange={onTourSchedulingToggle}
          />
        </Box>
        <FieldSet
          css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}
          legend={<Legend size="lg">Booking method</Legend>}
        >
          {TOUR_OPTIONS.map((tourOption, index) => (
            <ToggleCard
              data-testid={`CalendarSettings-TourType-${tourOption.id}`}
              css={{ marginBottom: index === TOUR_OPTIONS.length - 1 ? 'default' : 0 }}
              key={index}
              onClick={() => handleClickToggleCard(tourOption)}
              selected={tourOption.id === tourConfirmationType}
            >
              <TourConfirmationTypeCard layout={TOUR_CONFIRMATION_TYPE_LAYOUT.HORIZONTAL} tourOption={tourOption} />
            </ToggleCard>
          ))}
        </FieldSet>
        {canEditTourDuration && (
          <FormField
            data-testid="CalendarSettings-TourDuration"
            control={
              <DropdownSelect
                key={dropdownKey}
                onChange={handleTourDurationChange}
                options={[...TOUR_DURATION_DROPDOWN_OPTIONS]}
                renderInput={(renderProps: ComboboxInputPropsInterface) => {
                  const { ref: _ref, ...adornmentPropsWithoutRef } = renderProps;
                  return (
                    <Combobox.Input
                      data-testid="CalendarSettings-TourDuration-Dropdown"
                      {...adornmentPropsWithoutRef}
                    />
                  );
                }}
                showLabelForValue
                size="md"
                value={selectedTourDuration}
              />
            }
            css={{ marginBottom: 'default' }}
            label={<Label>Length of each tour</Label>}
          />
        )}
        <FieldSet
          css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}
          legend={<Legend size="lg">Calendar view preferences</Legend>}
        >
          <Box css={{ alignItems: 'center', display: 'flex', flexDirection: 'row', marginBottom: 'default' }}>
            <FormField
              control={
                <Combobox
                  editable={false}
                  onChange={handleChangeStartTime}
                  options={calendarTimeOptions}
                  renderAdornment={(adornmentProps) => {
                    const { ref: _ref, ...adornmentPropsWithoutRef } = adornmentProps;
                    return (
                      <Combobox.Adornment data-testid="CalendarSettings-StartTime-Input" {...adornmentPropsWithoutRef}>
                        <Icon render={<IconClockFilled />} />
                      </Combobox.Adornment>
                    );
                  }}
                  renderClear={() => null}
                  value={selectedStartTime}
                />
              }
              css={{ flexGrow: '1' }}
              label={<Label>Start day at</Label>}
            />
            <Icon css={{ marginLeft: 'tighter', marginRight: 'tighter', marginTop: 'loose' }} size="md">
              <IconArrowRightOutline />
            </Icon>
            <FormField
              control={
                <Combobox
                  editable={false}
                  onChange={handleChangeEndTime}
                  options={calendarTimeOptions}
                  renderAdornment={(adornmentProps) => {
                    const { ref: _ref, ...adornmentPropsWithoutRef } = adornmentProps;
                    return (
                      <Combobox.Adornment data-testid="CalendarSettings-EndTime-Input" {...adornmentPropsWithoutRef}>
                        <Icon render={<IconClockFilled />} />
                      </Combobox.Adornment>
                    );
                  }}
                  renderClear={() => null}
                  value={selectedEndTime}
                />
              }
              css={{ flexGrow: '1' }}
              label={<Label>End day at</Label>}
            />
          </Box>
        </FieldSet>
        <Text textStyle="body-bold">Time zone of property</Text>
        <Text>{timeZoneDisplayValue}</Text>
      </Box>

      <Modal.Root
        open={showTourDurationWarning}
        onOpenChange={handleCloseTourDurationWarningModal}
        shouldAwaitInteractionResponse
        size="xs"
      >
        <Modal.Portal>
          <Modal.Backdrop>
            {/* @ts-expect-error - focusManagerProps is requiring unneeded properties */}
            <Modal.Content focusManagerProps={{ returnFocus: false }}>
              <Modal.Header>
                <Heading level={1}>Confirm tour length change</Heading>
              </Modal.Header>
              <Divider />
              <Modal.Body>
                <Paragraph>
                  Adjusting the tour length may update your current availability. Confirm to proceed, then check your
                  calendar and make any changes if necessary.
                </Paragraph>
              </Modal.Body>
              <Divider />
              <Modal.Footer>
                <ButtonGroup aria-label="modal actions">
                  <Modal.Close>
                    <TextButton>Cancel</TextButton>
                  </Modal.Close>
                  <Button emphasis="filled" tone="brand" onClick={handleConfirmTourDuration}>
                    Confirm
                  </Button>
                </ButtonGroup>
              </Modal.Footer>
              <Modal.CloseButton />
            </Modal.Content>
          </Modal.Backdrop>
        </Modal.Portal>
      </Modal.Root>
    </Box>
  );
};

export default CalendarSettings;
